import { observable, action, computed } from 'mobx';
import objectPath from 'object-path';
import Alert from 'react-s-alert';

import {
  StoreRegistry,
  ComponentRegistry,
  FieldStoreRegistry,
} from 'core/registry';

import { StringFieldStore } from 'libs/forms/fieldStores';
import { EditorField } from 'libs/forms/views/fieldTypes/editor';
import { FileField } from 'libs/forms/views/fieldTypes/file';
import { OptionField } from 'libs/forms/views/fieldTypes/option';
import { PasswordField } from 'libs/forms/views/fieldTypes/password';
import { CheckboxField } from 'libs/forms/views/fieldTypes/checkbox';
import { SelectField } from 'libs/forms/views/fieldTypes/select';
import { MultiSelectField } from 'libs/forms/views/fieldTypes/multiselect';
import { ListField } from 'libs/forms/views/fieldTypes/list';
import { TextField } from 'libs/forms/views/fieldTypes/text';
import { DecimalField } from 'libs/forms/views/fieldTypes/decimal';
import { DateField } from 'libs/forms/views/fieldTypes/date';
import { PhoneField } from 'libs/forms/views/fieldTypes/phone';
import { TextAreaField } from 'libs/forms/views/fieldTypes/textArea';
import { RadioGroupAdapter } from 'libs/forms/views/fieldTypes/radioGroupAdapter';

import globalFields from 'libs/forms/fields';
import { ErrorCode } from 'core/errors';

ComponentRegistry.add('forms.FileField', FileField);
ComponentRegistry.add('forms.OptionField', OptionField);
ComponentRegistry.add('forms.CheckboxField', CheckboxField);
ComponentRegistry.add('forms.PasswordField', PasswordField);
ComponentRegistry.add('forms.SelectField', SelectField);
ComponentRegistry.add('forms.MultiSelectField', MultiSelectField);
ComponentRegistry.add('forms.ListField', ListField);
ComponentRegistry.add('forms.TextField', TextField);
ComponentRegistry.add('forms.DecimalField', DecimalField);
ComponentRegistry.add('forms.DateField', DateField);
ComponentRegistry.add('forms.PhoneField', PhoneField);
ComponentRegistry.add('forms.TextAreaField', TextAreaField);
ComponentRegistry.add('forms.EditorField', EditorField);
ComponentRegistry.add('forms.RadioGroupField', RadioGroupAdapter);

// TODO: Replace alert with mobx driven solution

const commaDelimitFields = (fields, finalDelimitor = 'and') => {
  return fields
    .slice(0, fields.length - 1)
    .join(', ')
    .concat(` ${finalDelimitor} ${fields[fields.length - 1]}`);
};

const isObject = function isObject(a) {
  return !!a && a.constructor === Object;
};

export function merge(confA, confB) {
  const confACopy = Object.assign({}, confA);
  // eslint-disable-next-line no-restricted-syntax
  for (const c of Object.keys(confB)) {
    if (confACopy[c] && isObject(confB[c])) {
      // eslint-disable-next-line no-param-reassign
      confACopy[c] = merge(Object.assign({}, confACopy[c]), confB[c]);
    } else {
      // eslint-disable-next-line no-param-reassign
      confACopy[c] = confB[c];
    }
  }
  return confACopy;
}

export function buildField(fieldConf = {}) {
  if (fieldConf.type) {
    if (globalFields[fieldConf.type] === undefined) {
      throw new Error(
        `No "fieldConf" with name "${fieldConf.type}" in globalFields.`,
      );
    }
    const globalConf = Object.assign(
      {},
      buildField(globalFields[fieldConf.type]),
    );
    return merge(globalConf, fieldConf);
  }
  return fieldConf;
}

export function buildConf(conf = {}, formName = '') {
  const built = [];
  const { fields } = conf;
  const formFields = objectPath.get(conf, `${formName}.fields`, []);
  // eslint-disable-next-line no-restricted-syntax
  for (let fieldName of formFields) {
    let target = '';
    if (fieldName.indexOf('.') !== -1) {
      const pathSegments = fieldName.split('.');
      fieldName = pathSegments[pathSegments.length - 1];
      target = pathSegments.slice(0, -1).join('.');
    }
    let fieldConf = fields[fieldName];
    if (fieldConf === undefined) {
      fieldConf = merge(globalFields[fieldName], {});
    }
    if (fieldConf === undefined) {
      console.error(`No "fieldConf" with name "${fieldName}" in ${formName}.`);
    }
    const builtField = buildField(fieldConf);
    builtField.optional = builtField.optional || false;
    if (!builtField.fieldName) {
      builtField.fieldName = fieldName;
    }
    builtField.targetFieldName = target;
    if (builtField.requireFileModel === undefined) {
      builtField.requireFileModel = true;
    }
    built.push(builtField);
  }
  return built;
}

export class FormStore {
  @observable posting = false;
  @observable disabled = false;
  @observable warning = null;
  @observable message = null;
  @observable conf;
  @observable target;

  constructor(target, conf, formName, autoFocusFirstField = true) {
    this.formName = formName;
    this.fieldConf = buildConf(conf, formName);
    this.conf = conf[formName] || {};
    if (!conf[formName]) {
      console.error(`No Form with name "${formName}" in conf.`);
    }
    this.target = target;
    this.fieldStores = new Map();
    const autoFocus =
      this.conf.autoFocus ||
      (autoFocusFirstField &&
        objectPath.get(this.fieldConf[0], 'fieldName', false));

    // eslint-disable-next-line no-restricted-syntax
    for (const [index, fieldConf] of this.fieldConf.entries()) {
      let Store = FieldStoreRegistry.get(fieldConf.fieldStore);
      if (Store === undefined) {
        if (fieldConf.component) {
          Store =
            ComponentRegistry.get(fieldConf.component).defaultStore ||
            StringFieldStore;
        } else {
          Store = StringFieldStore;
        }
      }
      const nextField = this.fieldConf[index + 1];
      const nextFieldName = nextField ? nextField.fieldName : '';
      const field = new Store(this, fieldConf, nextFieldName);
      field.autoFocus = autoFocus === field.fieldName;
      this.fieldStores.set(field.fieldName, field);
    }
  }

  prepare() {
    // eslint-disable-next-line no-restricted-syntax
    for (const fieldStore of this.fieldStores.values()) {
      if (fieldStore.prepare) {
        fieldStore.prepare();
      }
    }
  }

  @action clearDraft = () => {
    // eslint-disable-next-line no-restricted-syntax
    for (const fieldStore of this.fieldStores.values()) {
      fieldStore.clearDraft();
    }
  };

  @computed get isFocussed() {
    // eslint-disable-next-line no-restricted-syntax
    for (const fieldStore of this.fieldStores.values()) {
      if (fieldStore.isFocussed) {
        return true;
      }
    }
    return false;
  }

  @computed get isValid() {
    const hasOneOfValidation =
      this.conf.validIf && this.conf.validIf[0] === 'oneOf';
    if (hasOneOfValidation) {
      const emptyFields = [];
      this.conf.validIf[1].forEach(field => {
        if (this.fieldStores.get(field).value !== '') {
          emptyFields.push(field);
        }
      });
      return emptyFields.length > 0;
    }
    return this.invalidFields.length === 0;
  }

  @computed get invalidFields() {
    const fieldNames = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const [fieldName, field] of this.fieldStores.entries()) {
      if (field.validity[0] === false) {
        fieldNames.push([fieldName, field.validity[1]]);
      }
    }
    return fieldNames;
  }

  @computed get invalidFieldsNotReq() {
    const stillReqNames = this.stillRequiredFields.map(
      field => field.fieldName,
    );
    return this.invalidFields.filter(field => {
      if (!stillReqNames.includes(field[0])) {
        return true;
      }
      return false;
    });
  }

  @computed get stillRequired() {
    return !(this.stillRequiredFields.length === 0);
  }

  @computed get stillRequiredFields() {
    return Array.from(this.fieldStores.values()).filter(
      field => field.stillRequired,
    );
  }

  @computed get stillRequiredMessage() {
    if (!this.stillRequired) {
      return '';
    }
    if (this.stillRequiredFields.length === 1) {
      return `${this.stillRequiredFields[0].conf.label} is required.`;
    }
    const labels = this.stillRequiredFields.map(field => field.conf.label);
    return `${commaDelimitFields(labels)} are required.`;
  }

  @action SetPosting = bool => {
    this.posting = bool;
  };

  @action SetButtonDisabled = bool => {
    this.disabled = bool;
  };

  @action SetServerErrs = errs => {
    const nonValidationErrs = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const err of errs) {
      if (
        err.code === 'VALIDATION' ||
        err.extensions?.code === ErrorCode.VALIDATION
      ) {
        let fieldKey = err.key;
        if (!fieldKey) {
          fieldKey = err.extensions.field;
        }
        const keyField = this.fieldStores.get(fieldKey);
        if (keyField === undefined) {
          console.error(
            `No field with name "${err.key}" in ${
              this.formName
            }. Existing keys are: ${Array.from(this.fieldStores.keys()).join(
              ', ',
            )}`,
          );
        } else {
          keyField.SetServerValidation(err);
        }
      } else {
        nonValidationErrs.push(err);
      }
    }
    if (errs.length > nonValidationErrs.length) {
      this.alert();
    }
    return nonValidationErrs;
  };

  alert() {
    if (this.stillRequired) {
      Alert.error(this.stillRequiredMessage);
    }
    if (this.invalidFieldsNotReq.length !== 0) {
      // reverse since messages are displayed stacked bottom-left
      this.invalidFieldsNotReq.reverse().forEach(field => {
        Alert.error(field[1]);
      });
    }
    if (this.conf.validIf && this.conf.validIf[0] === 'oneOf') {
      const labels = this.conf.validIf[1].map(
        field => this.fieldStores.get(field).conf.label,
      );
      Alert.error(`${commaDelimitFields(labels, 'or')} is required.`);
    }
  }
}

StoreRegistry.add('forms.FormStore', FormStore);
